import React, { useEffect, useMemo } from 'react';
import Stack, { ContentStack } from '../../../../../../v2BaseComponents/Stack';
import { AnimateLoadedContent } from '../../../../../../v2BaseComponents/Loader';
import { getIntl } from '../../../../../../../utils/intl';
import InfoIcon from '../../../../../../v2BaseComponents/InfoIcon';
import { PromptActions } from '../../../../../../v2BaseComponents/Prompt';
import useOrganizationClientInvitations from '../../../../../../../hooks/api/useOrganizationClientInvitations';

const intl = getIntl('revoke-invitation-prompt-content', {
    revokeInvitations: 'Revoke Invitations',
    revokeInvitationsPrompt: 'Are you sure you want to revoke {count} invitations?',
    revoke: 'Revoke',
    cancel: 'Cancel',
});

export type RevokeInvitationsPromptContentOwnProps = {
    onClose: () => void;
    organizationClientRecordIds: string[] | undefined;
    onRevoke: (organizationClientInvitationIds: string[]) => void;
};

export type RevokeInvitationsPromptContentProps = RevokeInvitationsPromptContentOwnProps;

const Content: React.FC<{
    onRevoke: () => void;
    onClose: () => void;
    invitationCount: number;
}> = ({ onClose, onRevoke, invitationCount }) => {
    return (
        <ContentStack>
            <InfoIcon
                alertIconProps={{
                    name: 'AlertTriangle',
                    variant: 'warning',
                }}
                title={intl('revokeInvitations')}
                subText={intl('revokeInvitationsPrompt', {
                    count: invitationCount,
                })}
            />
            <PromptActions
                actions={[
                    {
                        title: intl('revoke'),
                        onPress: onRevoke,
                        icon: 'Trash2',
                        variant: 'alert',
                        testID: 'confirm-revoke-invitations',
                    },
                    {
                        title: intl('cancel'),
                        onPress: onClose,
                        variant: 'secondary',
                        icon: 'XCircle',
                        testID: 'cancel-revoke-invitations',
                    },
                ]}
            />
        </ContentStack>
    );
};

const RevokeInvitationsPromptContent: React.FC<RevokeInvitationsPromptContentProps> = ({
    onClose,
    organizationClientRecordIds,
    onRevoke,
}) => {
    const { isLoadingOrganizationClientInvitations, organizationClientInvitations } = useOrganizationClientInvitations(
        {},
    );

    const invitationsForClientRecords = useMemo(
        () =>
            organizationClientInvitations?.filter((invitation) =>
                organizationClientRecordIds?.includes(invitation.organizationClientRecordId),
            ),
        [organizationClientInvitations, organizationClientRecordIds],
    );

    useEffect(() => {
        if (!isLoadingOrganizationClientInvitations && !invitationsForClientRecords.length) {
            onClose();
        }
    }, [invitationsForClientRecords.length, isLoadingOrganizationClientInvitations, onClose]);

    return (
        <Stack vertical fullWidth style={{ minHeight: 200 }}>
            <AnimateLoadedContent isLoading={!organizationClientRecordIds}>
                {() =>
                    organizationClientRecordIds ? (
                        <Content
                            invitationCount={invitationsForClientRecords.length}
                            onRevoke={() => {
                                onRevoke(
                                    invitationsForClientRecords.map(
                                        (invitation) => invitation.organizationClientInvitationId,
                                    ),
                                );
                            }}
                            onClose={onClose}
                        />
                    ) : null
                }
            </AnimateLoadedContent>
        </Stack>
    );
};

export default React.memo(RevokeInvitationsPromptContent);
